// File: sliders/slider-types/FeaturedArticleCard.jsx
import { useNavigate } from 'react-router-dom';

import config from '@/config';
import useImageAccentColor from '@/hooks/useImageAccentColor';
import useStateHandler from '@/hooks/useStateHandler';
import Heading from '@/shared-components/text/components/Heading';
import Text from '@/shared-components/text/components/Text';
import { formatArticleDisplayDate } from '@/utils/articleDisplayDate';
import { formatArticleReadingTime } from '@/utils/articleReadingTime';
import { buildPublicContentPath } from '@/utils/contentRoutes';
import { resolveFollowState } from '@/utils/followState';

import './FeaturedArticleCard.css';

export default function FeaturedArticleCard({ article, onClickRead }) {
  const navigate = useNavigate();
  const { cacheHandler } = useStateHandler();
  const coverImageUrl = article.coverImageId
    ? `${config.app.imageServiceDomain}/${article.coverImageId}/w=600`
    : null;
  const accentColor = useImageAccentColor(coverImageUrl);

  const handleRead = (e) => {
    e.stopPropagation();
    if (onClickRead) {
      onClickRead();
    } else {
      navigate(buildPublicContentPath({ ...article, post_type: 'article' }, 'article'));
    }
  };

  // --- creator info ---

  const creator =
    article.creator ||
    article.article_creator ||
    article.gallery ||
    article.artist ||
    article.collector;
  const creatorId = creator?._id || article.creator_id || article.creatorId;
  const isFollowing = resolveFollowState({
    cacheHandler,
    userId: creatorId,
    imFollowing: creator?.im_following ?? article.creator_im_following ?? article.im_following,
  });

  const displayDate = formatArticleDisplayDate(article?.createdAt);
  const readingTime = formatArticleReadingTime(article);
    
  return (
    <div
      className={`featured-article-card__container ${isFollowing ? 'following' : ''}`}
      style={{
        '--post-card-hover-bg': accentColor.hoverBackground,
        '--post-card-hover-outline': accentColor.hoverOutline,
      }}
      onClick={handleRead}
    >
      <div className="featured-article-card__cover-wrapper">
        {article.coverImageId ? (
          <img
            className="featured-article-card__cover"
            src={coverImageUrl}
            alt={article.title || ''}
          />
        ) : (
          <div className="featured-article-card__cover-placeholder" />
        )}
      </div>

      <div className="featured-article-card__content">
        <Heading className="featured-article-card__title">{article.title}</Heading>
        <Text className="featured-article-card__description" paragraph>
          {article.description}
        </Text>

        <div className="featured-article-card__footer">
          <Text className="featured-article-card__timestamp" >
            {displayDate ? `${displayDate} · ${readingTime}` : readingTime}
          </Text>
        </div>
      </div>
    </div>
  );
}
